import React from 'react';
import { motion } from 'framer-motion';
import { Target, Calendar, Image, LayoutDashboard, Users, Sparkles, ShieldCheck } from 'lucide-react';
import landingData from '../landingData.json';

const icons = [
  <Target key="0" size={22} className="text-[#067978]" />,
  <Calendar key="1" size={22} className="text-[#067978]" />,
  <Image key="2" size={22} className="text-[#067978]" />,
  <LayoutDashboard key="3" size={22} className="text-[#067978]" />,
  <Users key="4" size={22} className="text-[#067978]" />,
  <Sparkles key="5" size={22} className="text-[#067978]" />
];

const BenefitsSection = () => {
  return (
    <section 
      className="py-24 sm:py-32 relative overflow-hidden bg-[#F0FAF8] border-t border-b border-gray-100" 
      id="benefits"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="flex justify-center mb-4">
            <div className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-widest uppercase px-4 py-1.5 rounded-full text-[#044E4D] bg-[#E0F2F1] border border-[#067978]/25">
              <ShieldCheck size={14} className="text-[#067978]" /> Six reasons to join
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#0F172A] tracking-tight mb-4 leading-tight">
            Built for local business.<br />
            <span className="text-[#067978]">Nothing else.</span>
          </h2>
          <p className="text-[#475569] font-normal text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            Every feature exists for one reason: more customers walking through your door, more often.
          </p>
        </div>

        {/* Zig-Zag Alternating River Timeline */}
        <div className="relative">
          {/* Central Vertical Connector Line */}
          <div className="absolute top-8 bottom-8 left-6 md:left-1/2 -translate-x-1/2 w-0.5 bg-[#067978]/20"></div>

          <div className="space-y-16 md:space-y-24">
            {landingData.benefitsData.map((item, idx) => {
              const isEven = idx % 2 === 0;

              return (
                <div 
                  key={idx}
                  className={`relative flex flex-col md:flex-row items-center ${
                    isEven ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  {/* Left / Right Content Block */}
                  <motion.div 
                    initial={{ opacity: 0, x: isEven ? 40 : -40, y: 20 }}
                    whileInView={{ opacity: 1, x: 0, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className={`w-full md:w-1/2 pl-16 md:pl-0 ${
                      isEven 
                        ? 'md:pl-16 text-left' 
                        : 'md:pr-16 md:text-right'
                    }`}
                  >
                    <div className={`space-y-3 flex flex-col ${isEven ? 'md:items-start' : 'md:items-end items-start'}`}>
                      {/* Badge Pill */}
                      <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-[#E0F2F1] border border-[#067978]/20 text-[10.5px] font-extrabold tracking-wider uppercase text-[#044E4D]">
                        {item.badge}
                      </div>

                      {/* Title */}
                      <h3 className="text-xl sm:text-2xl font-bold text-[#0F172A] tracking-tight">
                        {item.title}
                      </h3>

                      {/* Body */}
                      <p className="text-[#334155] text-sm sm:text-base leading-relaxed max-w-md font-normal">
                        {item.body}
                      </p>
                    </div>
                  </motion.div>

                  {/* Central Floating Node */}
                  <div className="absolute left-6 md:left-1/2 -translate-x-1/2 top-0 md:top-1/2 md:-translate-y-1/2 z-20 flex items-center justify-center">
                    <div className="relative group">
                      <div className="w-12 h-12 rounded-2xl bg-white border-2 border-[#067978] flex items-center justify-center text-[#067978] font-extrabold text-sm shadow-md group-hover:scale-110 group-hover:border-[#044E4D] group-hover:shadow-lg transition-all duration-300">
                        {icons[idx]}
                      </div>
                      <span className="absolute -top-2 -right-2 bg-[#067978] text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center shadow-sm">
                        0{idx + 1}
                      </span>
                    </div>
                  </div>

                  {/* Spacer Block for Desktop Layout Balance */}
                  <div className="hidden md:block w-1/2"></div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default BenefitsSection;
